import { Button, Container, Grid, makeStyles, Typography } from '@material-ui/core';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { CheckMark } from '../../../icons/CheckMark';
import { DownloadIcon } from '../../../icons/DownloadIcon';
import { getQualityScore } from '../Quality/getQualityScore/getQualityScore';
import { QualityScore } from '../Quality/Quality';
import { SmallError } from '../../../icons/SmallError';
import TestsPassed from './img-tests-passed.svg';
import { Loading } from '../../../icons/Loading';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
  resultContainer: {
    marginTop: '1em',
    '&:not(:last-child)': {
      paddingBottom: '1em',
      borderBottom: '0.1em solid #8891AA',
    },
    '@media screen and (max-height: 700px)': {
      marginTop: 'none',
    },
  },
  iconContainer: {
    display: 'flex',
    '& svg': {
      margin: '0.2em 0.8em 0 0',
    },
  },
  downloadButton: {
    marginRight: '1.5em',
    '& svg': {
      position: 'relative',
      left: '-5px',
    },
    '@media screen and (max-width: 351px)': {
      marginBottom: '10px',
    },
  },
  illustrationContainer: {
    display: 'flex',
    alignItems: 'center',
    '@media screen and (max-width: 950px)': {
      justifyContent: 'center',
      display: 'none',
    },
  },
  image: {
    width: '245px',
    height: '320px',
    '@media screen and (max-width: 950px)': {
      marginTop: '12px',
      width: '50vh',
      height: 'auto',
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  customTypography: {
    '@media screen and (max-height: 700px)': {
      marginBottom: '0px',
    },
  },
  gridContainer: {
    justifyContent: 'space-between',
    '@media screen and (max-height: 950px)': {
      height: 'calc(100vh - 100px)',
      overflow: 'auto',
      paddingTop: '50px',
      paddingBottom: '5px',
    },
  },
  button: {
    background: 'white',
    '@media screen and (max-width: 351px)': {
      paddingTop: '10px',
    },
  },
  loading: {
    animation: '$loading 1.5s linear infinite',
    display: 'flex',
  },
  '@keyframes loading': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  textBox: {
    width: '340px',
    height: '48px',
    textAlign: 'center',
  },
});

export function Results() {
  const { state, downloadFinalTestResults, dispatch } = useAppStateContext();
  const { totalQualityScore } = getQualityScore(state.bitrateTestUDP.reportUDP, state.bitrateTestTCP.reportTCP);
  const classes = useStyles();
  const { userAgentInfo } = useAppStateContext();
  const browser = userAgentInfo.browser;

  const connectivityTestPassed = state.sipConnectivityUSTestConnected && state.sipConnectivitySATestConnected;
  const codecsTestsPassed = state.videoCodecTestSuccessful && state.audioCodecTestSuccessful;
  const testsPassed =
    (totalQualityScore === QualityScore.Excellent || totalQualityScore === QualityScore.Good) &&
    connectivityTestPassed &&
    codecsTestsPassed;
  const qualityScore = QualityScore[totalQualityScore].toLowerCase();
  const browserTestPassed = browser.name === 'Chrome';
  const speedTestNotFinished: boolean =
    state.downloadSpeedResult === null && state.uploadSpeedResult === null && state.bitrateTestUDP.reportUDP === null;
  const { t } = useTranslation('', { keyPrefix: 'RESULTS' });
  const performance = qualityScore === 'excellent' ? t('RESULTS.GOOD') : t('RESULTS.OK');

  return speedTestNotFinished ? (
    <Container>
      <Grid container className={classes.container}>
        <Typography variant="h1" gutterBottom>
          {t('HANG_TIGHT')}
        </Typography>
        <Typography variant="body1" gutterBottom className={classes.textBox}>
          {t('CREATING_REPORT')}
        </Typography>
        <div className={classes.loading}>
          <Loading />
        </div>
      </Grid>
    </Container>
  ) : (
    <Container>
      <Grid container className={classes.gridContainer}>
        <Grid item md={5}>
          <Typography variant="h1" paragraph>
            {testsPassed ? t('ALL_TESTS_PASSED') : t('SOME_TESTS_FAILED')}
          </Typography>

          {testsPassed ? (
            <Typography variant="body1" paragraph className={classes.customTypography}>
              {t('DOWNLOAD_TEST_RESULTS_PASSED_DESCRIPTION')}
            </Typography>
          ) : (
            <Typography variant="body1" paragraph className={classes.customTypography}>
              {t('DOWNLOAD_TEST_RESULTS_FAILED_DESCRIPTION')}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            disableRipple
            className={classes.downloadButton}
            onClick={downloadFinalTestResults}
          >
            <DownloadIcon />
            {t('DOWNLOAD_TEST_RESULTS')}
          </Button>
          <Button
            color="primary"
            className={classes.button}
            variant="outlined"
            disableRipple
            onClick={() => window.location.reload()}
          >
            {t('RESTART_TESTS')}
          </Button>
          <Grid item md={5} className={classes.illustrationContainer}>
            {testsPassed ? <img src={TestsPassed} alt="Success" className={classes.image} /> : null}
          </Grid>
        </Grid>

        <Grid item md={5}>
          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {browserTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                {t('DEVICE/NETWORK_SETUP')}
              </Typography>
            </div>
            {browserTestPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('AUDIO_AND_VIDEO_SUCCESS')}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('AUDIO_AND_VIDEO_FAILED')}
              </Typography>
            )}
            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CameraTest })}
              style={{ marginRight: '1.5em' }}
            >
              {t('REVIEW_HARDWARE')}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.BrowserTest })}
            >
              {t('REVIEW_BROWSER')}
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {connectivityTestPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                {t('CONNECTIVITY')}
              </Typography>
            </div>

            {connectivityTestPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('CONNECTIVITY_SUCCESS')}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('CONNECTIVITY_FAILED')}
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Connectivity })}
            >
              {t('REVIEW_CONNECTIVITY')}
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {codecsTestsPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                {t('AUDIO/VIDEO_CODECS')}
              </Typography>
            </div>

            {codecsTestsPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('CODECS_SUCCESS')}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('CODECS_FAILED')}
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CodecsTest })}
            >
              {t('REVIEW_AUDIO/VIDEO_CODECS')}
            </Button>
          </div>

          <div className={classes.resultContainer}>
            <div className={classes.iconContainer}>
              {testsPassed ? <CheckMark /> : <SmallError />}
              <Typography variant="h3" paragraph className={classes.customTypography}>
                {t('QUALITY/PERFORMANCE')}
              </Typography>
            </div>

            {testsPassed ? (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('QUALITY_SUCCESS', {
                  qualityScore,
                  performance,
                })}
              </Typography>
            ) : (
              <Typography variant="body1" paragraph className={classes.customTypography}>
                {t('QUALITY_FAILED', {
                  qualityScore,
                })}
              </Typography>
            )}

            <Button
              color="primary"
              variant="outlined"
              disableRipple
              onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Quality })}
            >
              {t('REVIEW_PERFORMANCE')}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Container>
  );
}
